import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

const KLAVIYO_BACK_IN_STOCK_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';
const KLAVIYO_SUBSCRIPTION_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
const KLAVIYO_API_REVISION = '2025-01-15';

/**
 * Back in stock modal component for Klaviyo integration.
 *
 * @typedef {object} BackInStockModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} formContainer - The form container element.
 * @property {HTMLInputElement} emailInput - The email input.
 * @property {HTMLInputElement} [termsCheckbox] - The terms checkbox.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} submitText - The submit button text.
 * @property {HTMLElement} loadingIndicator - The loading indicator.
 * @property {HTMLElement} successMessage - The success message container.
 * @property {HTMLElement} errorMessage - The error message element.
 *
 * @extends Component<BackInStockModalRefs>
 */
class BackInStockModal extends Component {
  requiredRefs = ['dialog', 'formContainer', 'emailInput', 'submitButton', 'errorMessage'];

  #previousScrollY = 0;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Listen for variant updates to sync variant ID
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Gets the Klaviyo Company ID from the data attribute.
   * @returns {string}
   */
  get companyId() {
    return this.dataset.klaviyoCompanyId || '';
  }

  /**
   * Gets the Klaviyo List ID from the data attribute.
   * @returns {string}
   */
  get listId() {
    return this.dataset.klaviyoListId || '';
  }

  /**
   * Gets the product ID from the data attribute.
   * @returns {string}
   */
  get productId() {
    return this.dataset.productId || '';
  }

  /**
   * Gets the variant ID from the data attribute.
   * @returns {string}
   */
  get variantId() {
    return this.dataset.variantId || '';
  }

  /**
   * Gets the email error message from the data attribute.
   * @returns {string}
   */
  get errorEmailMessage() {
    return this.dataset.errorEmail || 'Please enter a valid email address';
  }

  /**
   * Gets the terms error message from the data attribute.
   * @returns {string}
   */
  get errorTermsMessage() {
    return this.dataset.errorTerms || 'Please accept the terms and conditions';
  }

  /**
   * Gets the API error message from the data attribute.
   * @returns {string}
   */
  get errorApiMessage() {
    return this.dataset.errorApi || 'Something went wrong. Please try again.';
  }

  /**
   * Shows the modal dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Store scroll position
    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      // Lock scroll
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.showModal();

      // Focus the email input
      this.refs.emailInput.focus();

      // Add event listeners
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the modal dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Remove event listeners
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    // Unlock scroll
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'auto' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // Reset form state
    this.#resetForm();
  };

  /**
   * Handles the form submission.
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event?.preventDefault();

    const { emailInput, termsCheckbox, submitButton, loadingIndicator, submitText } = this.refs;

    // Hide any previous errors
    this.#hideError();

    // Validate email
    if (!emailInput.value || !this.#isValidEmail(emailInput.value)) {
      this.#showError(this.errorEmailMessage);
      emailInput.focus();
      return;
    }

    // Validate terms if checkbox exists
    if (termsCheckbox && !termsCheckbox.checked) {
      this.#showError(this.errorTermsMessage);
      return;
    }

    // Show loading state
    submitButton.disabled = true;
    submitText.hidden = true;
    loadingIndicator.hidden = false;

    try {
      // Subscribe to back in stock notifications
      await this.#subscribeToBackInStock(emailInput.value);

      // If a list ID is configured, also subscribe the user to that list
      if (this.listId) {
        await this.#subscribeToList(emailInput.value);
      }

      this.#showSuccess();
    } catch (error) {
      console.error('Back in stock subscription failed:', error);
      this.#showError(this.errorApiMessage);
    } finally {
      submitButton.disabled = false;
      submitText.hidden = false;
      loadingIndicator.hidden = true;
    }
  };

  /**
   * Subscribes the user to back in stock notifications via Klaviyo.
   * @see https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
   * @param {string} email
   */
  async #subscribeToBackInStock(email) {
    /** @type {Record<string, { data: { type: string; id: string } }>} */
    const relationships = {
      variant: {
        data: {
          type: 'catalog-variant',
          id: `$shopify:::$default:::${this.variantId}`,
        },
      },
    };

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
              },
            },
          },
          channels: ['EMAIL'],
        },
        relationships,
      },
    };

    const response = await fetch(`${KLAVIYO_BACK_IN_STOCK_ENDPOINT}?company_id=${this.companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_API_REVISION,
      },
      body: JSON.stringify(payload),
    });

    // Klaviyo returns 202 for success with no response body
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData?.errors?.[0]?.detail || `Klaviyo API error: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response;
  }

  /**
   * Subscribes the user to a Klaviyo list.
   * @see https://developers.klaviyo.com/en/reference/create_client_subscription
   * @param {string} email
   */
  async #subscribeToList(email) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.listId,
            },
          },
        },
      },
    };

    const response = await fetch(`${KLAVIYO_SUBSCRIPTION_ENDPOINT}?company_id=${this.companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_API_REVISION,
      },
      body: JSON.stringify(payload),
    });

    // Klaviyo returns 202 for success with no response body
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData?.errors?.[0]?.detail || `Klaviyo API error: ${response.status}`;
      console.error('List subscription failed:', errorMessage);
      // Don't throw here - we don't want to fail the whole flow if list subscription fails
      // The back-in-stock subscription already succeeded
    }

    return response;
  }

  /**
   * Validates an email address.
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Shows an error message.
   * @param {string} message - The error message to display.
   */
  #showError(message) {
    const { errorMessage, emailInput } = this.refs;
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    }
    if (emailInput && errorMessage?.id) {
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.setAttribute('aria-describedby', errorMessage.id);
    }
  }

  /**
   * Hides the error message.
   */
  #hideError() {
    const { errorMessage, emailInput } = this.refs;
    if (errorMessage) {
      errorMessage.hidden = true;
      errorMessage.textContent = '';
    }
    if (emailInput) {
      emailInput.removeAttribute('aria-invalid');
      emailInput.removeAttribute('aria-describedby');
    }
  }

  /**
   * Shows the success state.
   */
  #showSuccess() {
    const { formContainer, successMessage } = this.refs;
    if (formContainer) formContainer.hidden = true;
    successMessage.hidden = false;

    // Auto-close after 3 seconds
    setTimeout(() => {
      this.closeDialog();
    }, 3000);
  }

  /**
   * Resets the form to its initial state.
   */
  #resetForm() {
    const { formContainer, successMessage, emailInput, termsCheckbox } = this.refs;

    if (formContainer) formContainer.hidden = false;
    if (emailInput) emailInput.value = '';
    if (successMessage) successMessage.hidden = true;
    this.#hideError();

    // Reset checkbox if it exists
    if (termsCheckbox) {
      termsCheckbox.checked = false;
    }
  }

  /**
   * Handles click events on the modal.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Handles keydown events on the modal.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    }
  };

  /**
   * Handles variant update events to sync the variant ID.
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { resource } = event.detail;
    if (resource?.id) {
      this.dataset.variantId = String(resource.id);
    }
  };
}

if (!customElements.get('back-in-stock-modal')) {
  customElements.define('back-in-stock-modal', BackInStockModal);
}

/**
 * Back in stock button component that triggers the modal.
 *
 * @typedef {object} BackInStockButtonRefs
 * @property {HTMLButtonElement} button - The button element.
 *
 * @extends Component<BackInStockButtonRefs>
 */
class BackInStockButton extends Component {
  /**
   * Handles the button click to open the modal.
   * @param {MouseEvent} event
   */
  handleClick(event) {
    event.preventDefault();

    const modalId = this.dataset.modalId;
    if (!modalId) return;

    /** @type {BackInStockModal | null} */
    const modal = document.querySelector(`#${modalId}`);
    modal?.showDialog();
  }
}

if (!customElements.get('back-in-stock-button')) {
  customElements.define('back-in-stock-button', BackInStockButton);
}

export { BackInStockModal, BackInStockButton };
